// ═══════════════════════════════════════════════════════════════
// Debris Portal — Ticket Tab (Driver / Crew)
// - View active tickets + stops with navigation links
// - Add pickup stops (address autocomplete + measurement)
// - Close Out flow: loading monitor sign + driver pickup sign
// - QR code display for disposal site monitor
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useDebrisStore } from '../../../store/debrisStore';
import { useDebrisOpsStore } from '../../../store/debrisOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { DEBRIS_TYPES, DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../../types/debris';
import type { DebrisZone, DebrisType, DebrisLoadTicket } from '../../../types/debris';
import type { PortalRole } from './DebrisPortal';
import { notifications } from '../../../lib/notifications';
import { AddressAutocomplete } from '../../../components/ui/AddressAutocomplete';
import { SignaturePad } from '../../../components/ui/SignaturePad';
import {
  IconPlus, IconQrcode, IconTruck, IconX,
  IconChevronDown, IconChevronUp, IconMapPin, IconNavigation,
  IconCamera, IconTrash, IconPhoto,
} from '@tabler/icons-react';
import QRCode from 'qrcode';

interface Props { zone: DebrisZone | null; role?: PortalRole | null; }

// ── Helpers ───────────────────────────────────────────────────

function parseStops(json: string | undefined): any[] {
  try { return JSON.parse(json || '[]'); } catch { return []; }
}

function mapsLink(address: string, lat?: number, lng?: number): string {
  if (lat && lng) return `https://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent(address)}`;
  return `https://maps.apple.com/?q=${encodeURIComponent(address)}`;
}

// ── Photo utilities ───────────────────────────────────────────

function fileToDataUrl(file: File): Promise<string> {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}

async function compressImage(dataUrl: string, maxPx = 1280, quality = 0.75): Promise<string> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxPx / Math.max(img.width, img.height), 1);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = dataUrl;
  });
}

// ── QR Code Drawer ────────────────────────────────────────────

function QRDrawer({ ticket, onClose }: { ticket: DebrisLoadTicket; onClose: () => void }) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const verifyUrl = `${window.location.origin}/debris/verify/${ticket.id}`;
  const stops = useMemo(() => parseStops(ticket.pickupStopsJson), [ticket.pickupStopsJson]);

  useEffect(() => {
    QRCode.toDataURL(verifyUrl, { width: 260, margin: 2, color: { dark: '#1B1C1C', light: '#ffffff' } })
      .then(setQrDataUrl).catch(() => {});
  }, [verifyUrl]);

  return (
    <div className="sp-drawer-overlay" onClick={onClose}>
      <div className="sp-drawer" onClick={e => e.stopPropagation()} style={{ paddingBottom: 80 }}>
        <div className="sp-drawer-handle" />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C', marginBottom: 2 }}>
            {ticket.ticketNumber || `Ticket #${ticket.id}`}
          </div>
          <div style={{ fontSize: 14, color: '#7B7D7F', marginBottom: 16 }}>
            {ticket.truckNumber ? `Truck ${ticket.truckNumber}` : 'No truck'}
            {ticket.debrisType ? ` · ${DEBRIS_TYPE_LABELS[ticket.debrisType] || ticket.debrisType}` : ''}
          </div>

          <div style={{ display: 'inline-block', padding: 16, background: '#fff', border: '2px solid #e2e8f0', borderRadius: 14, marginBottom: 16 }}>
            {qrDataUrl
              ? <img src={qrDataUrl} alt="Ticket QR" style={{ width: 200, height: 200, display: 'block' }} />
              : <div style={{ width: 200, height: 200, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#939598', fontSize: 14 }}>Generating…</div>
            }
          </div>

          <div style={{ background: '#fef3c7', borderRadius: 4, padding: '8px 16px', marginBottom: 16, fontSize: 12, color: '#92400e', textAlign: 'left' }}>
            <strong>For the monitor:</strong> Scan this code at the disposal site to verify the load.
          </div>

          {stops.length > 0 && (
            <div style={{ textAlign: 'left', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                {stops.length} Stop{stops.length !== 1 ? 's' : ''} — Route Summary
              </div>
              {stops.map((s: any, i: number) => (
                <div key={i} style={{ fontSize: 12, color: '#475569', padding: '5px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#939598', minWidth: 16 }}>{i + 1}</span>
                  <span style={{ flex: 1 }}>{s.address || 'No address'}</span>
                  {(s.estimatedLoadPercent || s.unitCount) && (
                    <span style={{ color: '#d97706', fontWeight: 600, fontSize: 12 }}>
                      {s.estimatedLoadPercent ? `${s.estimatedLoadPercent}%` : `${s.unitCount} units`}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ background: '#F8F8F8', borderRadius: 4, padding: '8px 16px', textAlign: 'left', marginBottom: 16 }}>
            {ticket.calculatedCubicYards && parseFloat(ticket.calculatedCubicYards) > 0 && (
              <div style={{ fontSize: 16, fontWeight: 800, color: '#d97706' }}>
                {parseFloat(ticket.calculatedCubicYards).toFixed(1)} CY total
              </div>
            )}
            {ticket.unitCount && <div style={{ fontSize: 16, fontWeight: 800, color: '#d97706' }}>{ticket.unitCount} units</div>}
            <div style={{ fontSize: 12, color: '#7B7D7F', marginTop: 2 }}>Status: <strong>{ticket.status?.replace(/_/g, ' ')}</strong></div>
          </div>

          <div style={{ fontSize: 12, color: '#939598', wordBreak: 'break-all', marginBottom: 16 }}>{verifyUrl}</div>
        </div>
        <button className="sp-btn sp-btn-secondary" style={{ width: '100%' }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// ── Add Stop Drawer ───────────────────────────────────────────

function AddStopDrawer({ ticket, onClose }: { ticket: DebrisLoadTicket; onClose: () => void }) {
  const { trucks } = useDebrisStore();
  const { updateLoadTicket } = useDebrisOpsStore();
  const { user, activeIncident } = useAuthStore();
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const [debrisType, setDebrisType] = useState<DebrisType | ''>(ticket.debrisType as DebrisType || '');
  const [loadPct, setLoadPct] = useState(50);
  const [unitCount, setUnitCount] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [diameterRange, setDiameterRange] = useState('');
  const [weight, setWeight] = useState('');
  const [notes, setNotes] = useState('');
  const [beforePhoto, setBeforePhoto] = useState<string | null>(null);
  const [afterPhoto, setAfterPhoto] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);

  const truck = useMemo(() => trucks.find(t => String(t.id) === ticket.truckId), [trucks, ticket.truckId]);
  const cfg = debrisType ? DEBRIS_TYPES[debrisType] : null;
  const mode = cfg?.measurementMode ?? 'volumetric';

  const calcCY = useMemo(() => {
    if (!truck || mode !== 'volumetric') return 0;
    const cap = parseFloat(truck.cubicYardCapacity) || 0;
    const adj = truck.hasTailgate === 'false' ? 0.85 : 1;
    return Math.round(cap * adj * (loadPct / 100) * 10) / 10;
  }, [truck, mode, loadPct]);

  // Auto-GPS on open
  useEffect(() => {
    setLocating(true);
    navigator.geolocation?.getCurrentPosition(
      pos => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setAddress(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, []);

  const captureGPS = () => {
    setLocating(true);
    navigator.geolocation?.getCurrentPosition(
      pos => {
        setLat(pos.coords.latitude); setLng(pos.coords.longitude);
        setAddress(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        setLocating(false);
      },
      () => { notifications.show({ message: 'GPS unavailable', color: '#F86A0B' }); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const handlePhoto = async (type: 'before' | 'after', file: File | null) => {
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    const compressed = await compressImage(dataUrl);
    if (type === 'before') setBeforePhoto(compressed);
    else setAfterPhoto(compressed);
  };

  const handleSave = async () => {
    if (!address.trim()) { notifications.show({ message: 'Enter an address or capture GPS', color: 'var(--color-error)' }); return; }
    if (!debrisType) { notifications.show({ message: 'Select debris type', color: 'var(--color-error)' }); return; }
    setSaving(true);
    try {
      const existing = parseStops(ticket.pickupStopsJson);
      const newStop = {
        id: `stop-${Date.now()}`,
        address: address.trim(), lat, lng,
        debrisType, measurementMode: mode,
        estimatedLoadPercent: mode === 'volumetric' ? loadPct : undefined,
        unitCount: mode === 'unit' ? parseInt(unitCount) || 0 : undefined,
        subcategory: subcategory || undefined,
        diameterRange: diameterRange || undefined,
        weight: mode === 'weight' ? parseFloat(weight) || 0 : undefined,
        weightUnit: cfg?.weightUnit ?? 'lbs',
        notes,
        beforePhotoData: beforePhoto || undefined,
        afterPhotoData: afterPhoto || undefined,
        timestamp: new Date().toISOString(),
      };
      const updatedStops = [...existing, newStop];

      const totalCY = updatedStops
        .filter(s => s.measurementMode === 'volumetric' && truck)
        .reduce((sum, s) => {
          const cap = parseFloat(truck!.cubicYardCapacity) || 0;
          const adj = truck!.hasTailgate === 'false' ? 0.85 : 1;
          return sum + Math.round(cap * adj * ((s.estimatedLoadPercent || 0) / 100) * 10) / 10;
        }, 0);
      const totalUnits = updatedStops.reduce((s, st) => s + (st.unitCount || 0), 0);

      await updateLoadTicket(ticket.id, {
        pickupStopsJson: JSON.stringify(updatedStops),
        calculatedCubicYards: totalCY > 0 ? String(totalCY) : ticket.calculatedCubicYards,
        unitCount: totalUnits > 0 ? String(totalUnits) : ticket.unitCount,
      } as any);

      notifications.show({ message: `Stop added${beforePhoto || afterPhoto ? ' with photos' : ''}`, color: 'var(--color-success)' });
      onClose();
    } catch {
      notifications.show({ message: 'Failed to add stop', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sp-drawer-overlay" onClick={onClose}>
      <div className="sp-drawer" onClick={e => e.stopPropagation()} style={{ paddingBottom: 80 }}>
        <div className="sp-drawer-handle" />
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C', marginBottom: 2 }}>Add Pickup Stop</div>
        <div style={{ fontSize: 12, color: '#7B7D7F', marginBottom: 8 }}>
          {ticket.ticketNumber || `#${ticket.id}`}{ticket.truckNumber ? ` · Truck ${ticket.truckNumber}` : ''}
        </div>

        {locating && (
          <div style={{ background: '#eff6ff', borderRadius: 4, padding: '8px 16px', fontSize: 12, color: '#1d4ed8', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconMapPin size={13} /> Getting GPS…
          </div>
        )}

        <div className="sp-field">
          <AddressAutocomplete
            value={address}
            onChange={(val, alat, alng) => { setAddress(val); if (alat) setLat(alat); if (alng) setLng(alng); }}
            label="Address / Location"
            required
            placeholder="Street address or cross streets"
          />
        </div>
        <button
          className="sp-btn sp-btn-secondary"
          style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 16, fontSize: 14 }}
          onClick={captureGPS} disabled={locating}
        >
          <IconMapPin size={13} />{locating ? 'Getting GPS…' : 'Recapture GPS'}
        </button>

        {/* Debris type */}
        <div className="sp-field">
          <label className="sp-label">Debris Type <span className="sp-req">*</span></label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(Object.keys(DEBRIS_TYPES) as DebrisType[]).map(type => {
              const typeCfg = DEBRIS_TYPES[type];
              const active = debrisType === type;
              return (
                <button key={type} onClick={() => setDebrisType(type)} style={{
                  padding: '8px 8px', border: '1.5px solid', borderRadius: 4,
                  fontSize: 12, fontWeight: active ? 700 : 500, cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left',
                  background: active ? `${typeCfg.color}18` : 'white',
                  color: active ? typeCfg.color : '#475569',
                  borderColor: active ? typeCfg.color : '#e2e8f0',
                }}>
                  {typeCfg.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Measurement */}
        {debrisType && mode === 'volumetric' && (
          <div className="sp-field">
            <label className="sp-label">
              Load %: <strong>{loadPct}%</strong>{calcCY > 0 ? <span style={{ color: '#d97706' }}> ≈ {calcCY} CY</span> : null}
            </label>
            <input type="range" min={5} max={100} step={5} value={loadPct}
              onChange={e => setLoadPct(Number(e.target.value))} style={{ width: '100%', marginBottom: 4 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#939598' }}>
              <span>5%</span><span>50%</span><span>100%</span>
            </div>
          </div>
        )}
        {debrisType && mode === 'unit' && (
          <>
            <div className="sp-field">
              <label className="sp-label">{cfg?.unitLabel || 'Count'} <span className="sp-req">*</span></label>
              <input className="sp-input" type="number" min={1} value={unitCount}
                onChange={e => setUnitCount(e.target.value)} placeholder="0" />
            </div>
            {(cfg as any)?.subcategories && (cfg as any).subcategories.length > 0 && (
              <div className="sp-field">
                <label className="sp-label">Subcategory</label>
                <select className="sp-input sp-select" value={subcategory} onChange={e => setSubcategory(e.target.value)}>
                  <option value="">— Select subcategory —</option>
                  {(cfg as any).subcategories.map((s: string) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {(cfg as any)?.hasDiameter && (
              <div className="sp-field">
                <label className="sp-label">Diameter Range</label>
                <select className="sp-input sp-select" value={diameterRange} onChange={e => setDiameterRange(e.target.value)}>
                  <option value="">— Select —</option>
                  {['6-12"', '12-24"', '24-36"', '36-48"', '48"+'].map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}
          </>
        )}
        {debrisType && mode === 'weight' && (
          <div className="sp-field">
            <label className="sp-label">Weight ({cfg?.weightUnit || 'lbs'}) <span className="sp-req">*</span></label>
            <input className="sp-input" type="number" min={0} value={weight}
              onChange={e => setWeight(e.target.value)} placeholder="0" />
          </div>
        )}

        {/* Before / After photos */}
        <div className="sp-field">
          <label className="sp-label">Photos</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(['before', 'after'] as const).map(type => {
              const photo = type === 'before' ? beforePhoto : afterPhoto;
              const setPhoto = type === 'before' ? setBeforePhoto : setAfterPhoto;
              return (
                <div key={type}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8, textTransform: 'capitalize' }}>{type}</div>
                  {photo ? (
                    <div style={{ position: 'relative' }}>
                      <img src={photo} alt={type}
                        style={{ width: '100%', borderRadius: 4, aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
                      <button onClick={() => setPhoto(null)} style={{
                        position: 'absolute', top: 4, right: 4,
                        background: 'rgba(220,38,38,0.9)', border: 'none', borderRadius: 4,
                        width: 26, height: 26, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                      }}>
                        <IconTrash size={13} />
                      </button>
                      <div style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.6)', borderRadius: 4, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <IconPhoto size={10} color="#fff" />
                        <span style={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>{type === 'before' ? 'BEFORE' : 'AFTER'}</span>
                      </div>
                    </div>
                  ) : (
                    <label style={{ cursor: 'pointer', display: 'block' }}>
                      <div style={{
                        border: '2px dashed #e2e8f0', borderRadius: 4, aspectRatio: '4/3',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', background: '#F8F8F8', gap: 4,
                      }}>
                        <IconCamera size={22} color="#939598" />
                        <span style={{ fontSize: 12, color: '#939598', fontWeight: 600 }}>Tap to capture</span>
                      </div>
                      <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                        onChange={e => handlePhoto(type, e.target.files?.[0] ?? null)} />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="sp-field">
          <label className="sp-label">Notes</label>
          <textarea className="sp-textarea" style={{ minHeight: 56 }} value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Conditions, access notes, hazards…" />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="sp-btn sp-btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="sp-btn sp-btn-primary" style={{ flex: 2 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Add Stop'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Close Out Drawer (collect signatures → in_transit) ────────

function CloseOutDrawer({ ticket, onClose }: { ticket: DebrisLoadTicket; onClose: () => void }) {
  const { trucks } = useDebrisStore();
  const { updateLoadTicket } = useDebrisOpsStore();
  const { user } = useAuthStore();

  const [hasMonitor, setHasMonitor] = useState<boolean | null>(null);
  const [monitorName, setMonitorName] = useState('');
  const [monitorLoadPct, setMonitorLoadPct] = useState(75);
  const [monitorSig, setMonitorSig] = useState('');
  const [driverName, setDriverName] = useState((user as any)?.email?.split('@')[0] ?? '');
  const [driverSig, setDriverSig] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');

  const stops = useMemo(() => parseStops(ticket.pickupStopsJson), [ticket.pickupStopsJson]);
  const verifyUrl = `${window.location.origin}/debris/verify/${ticket.id}`;

  const truck = useMemo(
    () => trucks.find(t => String(t.id) === ticket.truckId),
    [trucks, ticket.truckId],
  );

  useEffect(() => {
    if (done) {
      QRCode.toDataURL(verifyUrl, { width: 260, margin: 2 }).then(setQrDataUrl).catch(() => {});
    }
  }, [done, verifyUrl]);

  const handleSubmit = async () => {
    if (!driverSig) { notifications.show({ message: 'Driver signature is required', color: 'var(--color-error)' }); return; }
    if (hasMonitor && !monitorName.trim()) { notifications.show({ message: 'Enter loading monitor name', color: 'var(--color-error)' }); return; }
    if (hasMonitor && !monitorSig) { notifications.show({ message: 'Loading monitor signature required', color: 'var(--color-error)' }); return; }

    setSaving(true);
    try {
      await updateLoadTicket(ticket.id, {
        status: 'in_transit',
        pickupTime: new Date().toISOString(),
        driverPickupSignature: driverSig,
        loadingMonitorName: hasMonitor ? monitorName.trim() : '',
        loadingMonitorSignature: hasMonitor ? monitorSig : '',
        loadingLoadPct: hasMonitor ? String(monitorLoadPct) : '',
      } as any);
      setDone(true);
    } catch {
      notifications.show({ message: 'Failed to update ticket', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  // ── Done state: show QR ──
  if (done) {
    return (
      <div className="sp-drawer-overlay">
        <div className="sp-drawer" style={{ maxHeight: '90vh', overflowY: 'auto', textAlign: 'center' }}>
          <div className="sp-drawer-handle" />
          <div style={{ fontSize: 16, fontWeight: 800, color: '#059669', marginBottom: 4 }}>✓ In Transit</div>
          <div style={{ fontSize: 14, color: '#7B7D7F', marginBottom: 24 }}>
            Ticket {ticket.ticketNumber || `#${ticket.id}`} — proceed to disposal site
          </div>
          <div style={{ display: 'inline-block', padding: 16, background: '#fff', border: '2px solid #e2e8f0', borderRadius: 14, marginBottom: 16 }}>
            {qrDataUrl
              ? <img src={qrDataUrl} alt="QR" style={{ width: 200, height: 200, display: 'block' }} />
              : <div style={{ width: 200, height: 200, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#939598' }}>Loading…</div>
            }
          </div>
          <div style={{ background: '#fef3c7', borderRadius: 4, padding: '8px 16px', marginBottom: 24, fontSize: 12, color: '#92400e' }}>
            Show this QR to the tower monitor at the disposal site
          </div>
          <button className="sp-btn sp-btn-primary" style={{ width: '100%' }} onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-drawer-overlay" onClick={onClose}>
      <div className="sp-drawer" onClick={e => e.stopPropagation()} style={{ paddingBottom: 100 }}>
        <div className="sp-drawer-handle" />
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C', marginBottom: 2 }}>Close Out Ticket</div>
        <div style={{ fontSize: 12, color: '#7B7D7F', marginBottom: 16 }}>
          {ticket.ticketNumber || `#${ticket.id}`}
          {truck ? ` · Truck ${truck.truckNumber} (${truck.cubicYardCapacity} CY)` : ''}
        </div>

        {/* Stop summary */}
        {stops.length > 0 && (
          <div className="sp-card" style={{ marginBottom: 16 }}>
            <div className="sp-card-title">{stops.length} Stop{stops.length !== 1 ? 's' : ''} — Confirm Complete</div>
            {stops.map((s: any, i: number) => (
              <div key={i} style={{ fontSize: 12, color: '#475569', padding: '4px 0', borderBottom: i < stops.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', gap: 8 }}>
                <span style={{ fontWeight: 700, color: '#939598', minWidth: 16 }}>{i + 1}</span>
                <span style={{ flex: 1 }}>{s.address || 'No address'}</span>
                {s.estimatedLoadPercent && <span style={{ color: '#d97706', fontWeight: 600 }}>{s.estimatedLoadPercent}%</span>}
                {s.unitCount > 0 && <span style={{ color: '#d97706', fontWeight: 600 }}>{s.unitCount} units</span>}
              </div>
            ))}
            {ticket.calculatedCubicYards && parseFloat(ticket.calculatedCubicYards) > 0 && (
              <div style={{ marginTop: 8, fontWeight: 800, color: '#d97706', fontSize: 14 }}>
                Total: {parseFloat(ticket.calculatedCubicYards).toFixed(1)} CY
              </div>
            )}
          </div>
        )}

        {/* Loading monitor section */}
        <div className="sp-card" style={{ marginBottom: 16 }}>
          <div className="sp-card-title">Loading Monitor</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: hasMonitor !== null ? 12 : 0 }}>
            {([true, false] as const).map(v => (
              <button key={String(v)} onClick={() => setHasMonitor(v)} style={{
                flex: 1, padding: '8px 0', border: '1.5px solid', borderRadius: 4,
                fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                background: hasMonitor === v ? (v ? '#059669' : '#7B7D7F') : 'white',
                color: hasMonitor === v ? 'white' : '#475569',
                borderColor: hasMonitor === v ? (v ? '#059669' : '#7B7D7F') : '#e2e8f0',
              }}>
                {v ? '✓ Monitor Present' : 'No Monitor'}
              </button>
            ))}
          </div>

          {hasMonitor === true && (
            <>
              <div className="sp-field">
                <label className="sp-label">Monitor Name <span className="sp-req">*</span></label>
                <input className="sp-input" value={monitorName} onChange={e => setMonitorName(e.target.value)} placeholder="Full name or badge ID" />
              </div>
              <div className="sp-field">
                <label className="sp-label">Monitor Load Estimate: <strong>{monitorLoadPct}%</strong></label>
                <input type="range" min={5} max={100} step={5} value={monitorLoadPct}
                  onChange={e => setMonitorLoadPct(Number(e.target.value))} style={{ width: '100%' }} />
              </div>
              <SignaturePad
                label="Loading Monitor Signature *"
                onCapture={setMonitorSig}
              />
            </>
          )}
        </div>

        {/* Driver signature */}
        <div className="sp-card" style={{ marginBottom: 16 }}>
          <div className="sp-card-title">Driver Sign-off</div>
          <div className="sp-field">
            <label className="sp-label">Driver Name</label>
            <input className="sp-input" value={driverName} onChange={e => setDriverName(e.target.value)} placeholder="Your name" />
          </div>
          <SignaturePad
            label="Driver Signature (Pickup) *"
            onCapture={setDriverSig}
          />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="sp-btn sp-btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="sp-btn sp-btn-primary" style={{ flex: 2 }} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Submitting…' : '✓ Mark In Transit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── New Ticket Drawer ─────────────────────────────────────────

function NewTicketDrawer({ zone, onClose }: { zone: DebrisZone | null; onClose: () => void }) {
  const { trucks, zones } = useDebrisStore();
  const { createLoadTicket } = useDebrisOpsStore();
  const { user, activeIncident } = useAuthStore();
  const [truckId, setTruckId] = useState('');
  const [debrisType, setDebrisType] = useState<DebrisType | ''>('');
  const [assignedZoneId, setAssignedZoneId] = useState<string>(() => {
    if (zone) return String(zone.id);
    const first = zones.find(z => z.status === 'active') ?? zones[0];
    return first ? String(first.id) : '';
  });
  const [saving, setSaving] = useState(false);

  const certTrucks = useMemo(
    () => trucks.filter(t => t.status === 'certified' || t.status === 'standby'),
    [trucks],
  );
  const selectedTruck = useMemo(() => trucks.find(t => String(t.id) === truckId), [trucks, truckId]);

  const handleCreate = async () => {
    if (!truckId) { notifications.show({ message: 'Select a truck', color: 'var(--color-error)' }); return; }
    setSaving(true);
    try {
      await createLoadTicket({
        incidentId: String(activeIncident?.id ?? ''),
        zoneId: zone ? String(zone.id) : assignedZoneId,
        truckId,
        truckNumber: selectedTruck?.truckNumber || '',
        debrisType: debrisType || 'vegetative',
        measurementMode: debrisType ? (DEBRIS_TYPES[debrisType]?.measurementMode ?? 'volumetric') : 'volumetric',
        status: 'draft',
        pickupTime: new Date().toISOString(),
        loadingMonitorName: (user as any)?.email ?? '',
        calculatedCubicYards: '0',
        unitCount: '',
        weight: '',
        pickupStopsJson: '[]',
      } as any);
      notifications.show({ message: 'Ticket created — add stops as you collect debris', color: 'var(--color-success)' });
      onClose();
    } catch {
      notifications.show({ message: 'Failed to create ticket', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sp-drawer-overlay" onClick={onClose}>
      <div className="sp-drawer" onClick={e => e.stopPropagation()} style={{ paddingBottom: 80 }}>
        <div className="sp-drawer-handle" />
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C', marginBottom: 2 }}>New Ticket</div>
        <div style={{ fontSize: 12, color: '#7B7D7F', marginBottom: 16 }}>
          {zone ? `Zone: ${zone.zoneName || zone.zoneCode}` : 'All zones — assign below if needed'}
        </div>

        <div className="sp-field">
          <label className="sp-label">Truck <span className="sp-req">*</span></label>
          <select className="sp-input sp-select" value={truckId} onChange={e => setTruckId(e.target.value)}>
            <option value="">— Select certified truck —</option>
            {certTrucks.map(t => (
              <option key={t.id} value={String(t.id)}>Truck {t.truckNumber} · {t.cubicYardCapacity} CY · {t.truckType}</option>
            ))}
          </select>
          {certTrucks.length === 0 && (
            <div style={{ fontSize: 12, color: '#B12525', marginTop: 4 }}>No certified trucks available.</div>
          )}
        </div>

        {!zone && (
          <div className="sp-field">
            <label className="sp-label">Zone Assignment</label>
            <select className="sp-input sp-select" value={assignedZoneId} onChange={e => setAssignedZoneId(e.target.value)}>
              <option value="">— Auto-assign —</option>
              {zones.filter(z => z.status === 'active').map(z => (
                <option key={z.id} value={String(z.id)}>{z.zoneName || z.zoneCode}</option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: '#939598', marginTop: 3 }}>Your road monitor can reassign if needed</div>
          </div>
        )}

        {selectedTruck && (
          <div style={{ background: '#F8F8F8', borderRadius: 4, padding: '8px 8px', fontSize: 12, color: '#475569', marginBottom: 16 }}>
            Capacity: <strong>{selectedTruck.cubicYardCapacity} CY</strong>
            {selectedTruck.hasTailgate === 'false' && ' · No tailgate (15% FEMA reduction)'}
          </div>
        )}

        <div className="sp-field">
          <label className="sp-label">Primary Debris Type</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(Object.keys(DEBRIS_TYPES) as DebrisType[]).map(type => {
              const cfg = DEBRIS_TYPES[type];
              const active = debrisType === type;
              return (
                <button key={type} onClick={() => setDebrisType(type)} style={{
                  padding: '8px 8px', border: '1.5px solid', borderRadius: 4,
                  fontSize: 12, fontWeight: active ? 700 : 500, cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left',
                  background: active ? `${cfg.color}18` : 'white',
                  color: active ? cfg.color : '#475569',
                  borderColor: active ? cfg.color : '#e2e8f0',
                }}>
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '8px 16px', fontSize: 12, color: '#15803d', marginBottom: 16 }}>
          <strong>How it works:</strong> Create the ticket now. Add pickup stops as you collect debris. When the truck is full, tap <strong>Close Out</strong> to sign and mark it in transit — then show the QR code to the tower monitor at disposal.
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="sp-btn sp-btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="sp-btn sp-btn-primary" style={{ flex: 2 }} onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating…' : 'Create Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ticket Detail (full-screen) ───────────────────────────────

function TicketDetail({ ticket, zone, role, onClose, onAddStop, onCloseOut, onShowQR }: {
  ticket: DebrisLoadTicket;
  zone: DebrisZone | null;
  role?: PortalRole | null;
  onClose: () => void;
  onAddStop: () => void;
  onCloseOut: () => void;
  onShowQR: () => void;
}) {
  const { trucks } = useDebrisStore();
  const { updateLoadTicket } = useDebrisOpsStore();
  const stops = useMemo(() => parseStops(ticket.pickupStopsJson), [ticket.pickupStopsJson]);
  const truck = useMemo(() => trucks.find(t => String(t.id) === ticket.truckId), [trucks, ticket.truckId]);

  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  const canOverride = role === 'road_monitor' || role === 'tower_monitor';

  const STATUSES = [
    { value: 'draft',       label: 'Draft',       color: '#4B3FFF' },
    { value: 'in_transit',  label: 'In Transit',  color: '#d97706' },
    { value: 'at_site',     label: 'At Site',     color: '#7c3aed' },
    { value: 'verified',    label: 'Verified',    color: '#059669' },
    { value: 'delivered',   label: 'Delivered',   color: '#059669' },
    { value: 'voided',      label: 'Voided',      color: '#B12525' },
  ];

  const handleStatusChange = async (newStatus: string) => {
    setChangingStatus(true);
    try {
      await updateLoadTicket(ticket.id, { status: newStatus } as any);
      notifications.show({ message: `Status → ${newStatus.replace(/_/g, ' ')}`, color: 'var(--color-success)' });
      setShowStatusPicker(false);
      onClose();
    } catch {
      notifications.show({ message: 'Failed to update status', color: 'var(--color-error)' });
    } finally {
      setChangingStatus(false);
    }
  };

  const isDraft = ticket.status === 'draft';
  const isInTransit = ticket.status === 'in_transit';

  const STATUS_COLOR: Record<string, string> = {
    draft: '#4B3FFF', in_transit: '#d97706', at_site: '#7c3aed',
    verified: '#059669', delivered: '#059669', voided: '#B12525',
  };
  const sc = STATUS_COLOR[ticket.status] || '#939598';
  const color = DEBRIS_TYPE_COLORS[ticket.debrisType] || '#7B7D7F';

  const totalCY = parseFloat(ticket.calculatedCubicYards || '0');
  const totalUnits = parseInt(ticket.unitCount || '0');
  const totalWeight = parseFloat(ticket.weight || '0');

  return (
    <div style={{
      position: 'fixed', top: 0, bottom: 0, zIndex: 300,
      left: '50%', transform: 'translateX(-50%)',
      width: '100%', maxWidth: 480,
      background: '#F8F8F8', display: 'flex', flexDirection: 'column',
      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    }}>
      {/* Header bar */}
      <div style={{
        background: '#1e293b', color: '#fff', padding: '16px 16px',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
          <IconX size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{ticket.ticketNumber || `Ticket #${ticket.id}`}</div>
          <div style={{ fontSize: 12, color: '#939598' }}>{zone ? (zone.zoneName || zone.zoneCode) : 'All Zones'}</div>
        </div>
        {canOverride ? (
          <button onClick={() => setShowStatusPicker(true)} style={{
            fontSize: 12, fontWeight: 700, color: sc, background: `${sc}25`,
            padding: '4px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit',
          }}>
            {ticket.status?.replace(/_/g, ' ')} <IconChevronDown size={11} />
          </button>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 700, color: sc, background: `${sc}25`, padding: '4px 8px', borderRadius: 4 }}>
            {ticket.status?.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      {/* Status override picker — monitors only */}
      {showStatusPicker && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10,
          background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end',
        }} onClick={() => setShowStatusPicker(false)}>
          <div style={{
            background: '#fff', borderRadius: '4px 4px 0 0', width: '100%',
            padding: '24px 16px 40px',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 36, height: 4, background: '#cbd5e1', borderRadius: 4, margin: '0 auto 16px' }} />
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C', marginBottom: 2 }}>Override Status</div>
            <div style={{ fontSize: 12, color: '#7B7D7F', marginBottom: 16 }}>
              {ticket.ticketNumber || `Ticket #${ticket.id}`} · monitor override
            </div>
            {STATUSES.map(s => {
              const isCurrent = s.value === ticket.status;
              return (
                <button key={s.value} onClick={() => !isCurrent && handleStatusChange(s.value)}
                  disabled={changingStatus || isCurrent}
                  style={{
                    width: '100%', padding: '16px 16px', marginBottom: 8,
                    background: isCurrent ? `${s.color}12` : '#fff',
                    border: `1.5px solid ${isCurrent ? s.color : '#e2e8f0'}`,
                    borderRadius: 4, cursor: isCurrent ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
                    opacity: changingStatus && !isCurrent ? 0.5 : 1,
                    textAlign: 'left',
                  }}>
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: isCurrent ? s.color : '#1B1C1C', flex: 1 }}>
                    {s.label}
                  </span>
                  {isCurrent && (
                    <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>Current</span>
                  )}
                </button>
              );
            })}
            <button className="sp-btn sp-btn-secondary" style={{ marginTop: 8 }} onClick={() => setShowStatusPicker(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 100px' }}>

        {/* Truck + debris type info */}
        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 4, padding: '16px 16px', marginBottom: 16, borderLeft: `4px solid ${color}` }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: '#939598', fontWeight: 600, marginBottom: 2 }}>TRUCK</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1B1C1C' }}>
                {truck ? `${truck.truckNumber}` : ticket.truckNumber || '—'}
              </div>
              {truck && <div style={{ fontSize: 12, color: '#7B7D7F' }}>{truck.cubicYardCapacity} CY capacity</div>}
            </div>
            <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: 16 }}>
              <div style={{ fontSize: 12, color: '#939598', fontWeight: 600, marginBottom: 2 }}>DEBRIS TYPE</div>
              <div style={{ fontSize: 14, fontWeight: 700, color }}>
                {DEBRIS_TYPE_LABELS[ticket.debrisType] || ticket.debrisType || '—'}
              </div>
            </div>
            {(totalCY > 0 || totalUnits > 0 || totalWeight > 0) && (
              <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: 16, marginLeft: 'auto' }}>
                <div style={{ fontSize: 12, color: '#939598', fontWeight: 600, marginBottom: 2 }}>TOTAL</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#d97706' }}>
                  {totalCY > 0 ? `${totalCY.toFixed(1)} CY` : totalUnits > 0 ? `${totalUnits} units` : `${totalWeight} lbs`}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Workflow hint for drivers */}
        {isDraft && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '16px 16px', marginBottom: 16, fontSize: 14, color: '#1d4ed8' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>🚛 Driver Instructions</div>
            <div style={{ lineHeight: 1.5 }}>
              Drive through zone <strong>{zone ? (zone.zoneName || zone.zoneCode) : 'your assigned zone'}</strong> and collect debris.
              Tap <strong>+ Add Stop</strong> each time you load up at a new address.
              When the truck is full or your route is done, tap <strong>Close Out</strong> to sign and head to the disposal site.
            </div>
          </div>
        )}

        {isInTransit && (
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '16px 16px', marginBottom: 16, fontSize: 14, color: '#92400e' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>📍 Heading to Disposal</div>
            <div>Ticket is in transit. Show the <strong>QR code</strong> below to the tower monitor when you arrive.</div>
          </div>
        )}

        {/* Stops */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1B1C1C' }}>
              Pickup Stops ({stops.length})
            </div>
            {isDraft && (
              <button
                onClick={onAddStop}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px',
                  background: '#4B3FFF', color: '#fff', border: 'none', borderRadius: 4,
                  fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                <IconPlus size={14} /> Add Stop
              </button>
            )}
          </div>

          {stops.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px', background: '#fff', border: '2px dashed #e2e8f0', borderRadius: 4, color: '#939598' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📍</div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>No stops yet</div>
              <div style={{ fontSize: 14, marginBottom: 16 }}>Drive to your first pickup location and tap <strong>Add Stop</strong>.</div>
              {isDraft && (
                <button onClick={onAddStop} className="sp-btn sp-btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <IconPlus size={14} /> Add First Stop
                </button>
              )}
            </div>
          ) : stops.map((s: any, i: number) => {
            const stopColor = DEBRIS_TYPE_COLORS[s.debrisType] || color;
            return (
              <div key={i} style={{
                background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 4,
                marginBottom: 8, overflow: 'hidden',
              }}>
                {/* Stop header */}
                <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
                  {/* Number badge */}
                  <div style={{
                    background: stopColor, color: '#fff', width: 36, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 800, flexShrink: 0,
                  }}>
                    {i + 1}
                  </div>
                  {/* Address + measurement */}
                  <div style={{ flex: 1, padding: '8px 16px' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1B1C1C', marginBottom: 2 }}>
                      {s.address || 'No address recorded'}
                    </div>
                    <div style={{ fontSize: 12, color: '#7B7D7F' }}>
                      {DEBRIS_TYPE_LABELS[s.debrisType] || s.debrisType}
                      {s.estimatedLoadPercent ? ` · ${s.estimatedLoadPercent}% load` : ''}
                      {s.unitCount > 0 ? ` · ${s.unitCount} units` : ''}
                      {s.weight > 0 ? ` · ${s.weight} ${s.weightUnit || 'lbs'}` : ''}
                    </div>
                    {s.notes && (
                      <div style={{ fontSize: 12, color: '#939598', marginTop: 3, fontStyle: 'italic' }}>{s.notes}</div>
                    )}
                    {s.timestamp && (
                      <div style={{ fontSize: 12, color: '#939598', marginTop: 2 }}>
                        {new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                </div>
                {/* Photos strip */}
                {(s.beforePhotoData || s.afterPhotoData) && (
                  <div style={{ display: 'flex', gap: 8, padding: '8px 16px', borderTop: '1px solid #f1f5f9' }}>
                    {s.beforePhotoData && (
                      <div style={{ position: 'relative', flex: 1 }}>
                        <img src={s.beforePhotoData} alt="Before"
                          style={{ width: '100%', borderRadius: 4, aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
                        <div style={{ position: 'absolute', bottom: 3, left: 3, background: 'rgba(0,0,0,0.6)', borderRadius: 4, padding: '1px 5px', fontSize: 12, color: '#fff', fontWeight: 700 }}>BEFORE</div>
                      </div>
                    )}
                    {s.afterPhotoData && (
                      <div style={{ position: 'relative', flex: 1 }}>
                        <img src={s.afterPhotoData} alt="After"
                          style={{ width: '100%', borderRadius: 4, aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
                        <div style={{ position: 'absolute', bottom: 3, left: 3, background: 'rgba(5,150,105,0.85)', borderRadius: 4, padding: '1px 5px', fontSize: 12, color: '#fff', fontWeight: 700 }}>AFTER</div>
                      </div>
                    )}
                  </div>
                )}
                {/* Navigate button — full width, easy to tap */}
                {s.address && (
                  <a
                    href={mapsLink(s.address, s.lat, s.lng)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '10px', borderTop: '1px solid #f1f5f9',
                      background: '#F8F8F8', color: '#4B3FFF', textDecoration: 'none',
                      fontSize: 14, fontWeight: 700,
                    }}
                  >
                    <IconNavigation size={15} /> Navigate to this address
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {/* Signature status */}
        {(ticket.loadingMonitorSignature || ticket.driverPickupSignature || ticket.towerMonitorSignature || ticket.driverDeliverySignature) && (
          <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 4, padding: '16px 16px', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1B1C1C', marginBottom: 8 }}>Signatures</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { key: 'loadingMonitorSignature', label: 'Loading Monitor', name: ticket.loadingMonitorName },
                { key: 'driverPickupSignature',   label: 'Driver (Pickup)',  name: '' },
                { key: 'towerMonitorSignature',   label: 'Tower Monitor',   name: ticket.towerMonitorName || ticket.monitorName },
                { key: 'driverDeliverySignature', label: 'Driver (Delivery)',name: '' },
              ].map(({ key, label, name }) => {
                const signed = !!(ticket as any)[key];
                return (
                  <div key={key} style={{
                    padding: '8px 8px', borderRadius: 4, background: signed ? '#f0fdf4' : '#F8F8F8',
                    border: `1px solid ${signed ? '#bbf7d0' : '#e2e8f0'}`,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: signed ? '#059669' : '#939598', marginBottom: 2 }}>
                      {signed ? '✓ ' : '○ '}{label}
                    </div>
                    {name && <div style={{ fontSize: 12, color: '#475569' }}>{name}</div>}
                    {signed && !name && <div style={{ fontSize: 12, color: '#059669' }}>Signed</div>}
                    {!signed && <div style={{ fontSize: 12, color: '#939598' }}>Not signed</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sticky action bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: '#fff', borderTop: '1.5px solid #e2e8f0',
        padding: '16px 16px', display: 'flex', gap: 8,
        
      }}>
        <button onClick={onShowQR} style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '8px 16px',
          background: isInTransit ? '#059669' : '#1e293b', color: '#fff',
          border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
        }}>
          <IconQrcode size={16} /> QR
        </button>

        {isDraft && (
          <>
            <button onClick={onAddStop} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px', background: '#f1f5f9', color: '#475569',
              border: '1.5px solid #e2e8f0', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              <IconPlus size={15} /> Add Stop
            </button>
            <button onClick={onCloseOut} style={{
              flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px', background: '#d97706', color: '#fff',
              border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              <IconTruck size={15} /> Close Out & Sign
            </button>
          </>
        )}

        {isInTransit && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#d97706', fontWeight: 700 }}>
            En route to disposal site
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Ticket Tab ───────────────────────────────────────────

export default function DebrisPortalTicket({ zone, role }: Props) {
  const { loadTickets, updateLoadTicket } = useDebrisOpsStore();
  const { activeIncident } = useAuthStore();
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [qrTicket, setQrTicket] = useState<DebrisLoadTicket | null>(null);
  const [addStopTicket, setAddStopTicket] = useState<DebrisLoadTicket | null>(null);
  const [closeOutTicket, setCloseOutTicket] = useState<DebrisLoadTicket | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [transitingId, setTransitingId] = useState<number | null>(null);
  const [detailTicket, setDetailTicket] = useState<DebrisLoadTicket | null>(null);

  const STATUS_COLOR: Record<string, string> = {
    draft: '#4B3FFF', in_transit: '#d97706', at_site: '#7c3aed',
    verified: '#059669', delivered: '#059669', voided: '#B12525',
  };

  const zoneTickets = useMemo(
    () => zone
      ? loadTickets.filter(t => t.zoneId === String(zone.id))
      : loadTickets.filter(t => !activeIncident || t.incidentId === String(activeIncident.id)),
    [loadTickets, zone, activeIncident],
  );

  const activeTickets = useMemo(
    () => zoneTickets
      .filter(t => t.status === 'draft' || t.status === 'in_transit' || t.status === 'at_site')
      .sort((a, b) => (b.pickupTime || '').localeCompare(a.pickupTime || '')),
    [zoneTickets],
  );

  const completedTickets = useMemo(
    () => zoneTickets
      .filter(t => t.status === 'verified' || t.status === 'delivered' || t.status === 'voided')
      .sort((a, b) => (b.pickupTime || '').localeCompare(a.pickupTime || ''))
      .slice(0, 10),
    [zoneTickets],
  );

  return (
    <div>
      {/* Zone badge + new ticket */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1, background: '#fef3c7', borderRadius: 4, padding: '8px 16px', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
          {zone ? `Zone: ${zone.zoneName || zone.zoneCode}` : 'All Zones — your tickets'}
        </div>
        <button
          className="sp-btn sp-btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px', flexShrink: 0 }}
          onClick={() => setShowNewTicket(true)}
        >
          <IconPlus size={14} /> New
        </button>
      </div>

      {/* Empty state */}
      {activeTickets.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 16px', color: '#7B7D7F' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🚛</div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>No Active Tickets</div>
          <div style={{ fontSize: 14, marginBottom: 16 }}>Create a ticket to start tracking a load for this zone.</div>
          <button className="sp-btn sp-btn-primary" onClick={() => setShowNewTicket(true)}>
            <IconPlus size={14} style={{ display: 'inline', marginRight: 4 }} /> New Ticket
          </button>
        </div>
      )}

      {/* Active ticket cards */}
      {activeTickets.map(t => {
        const stops = parseStops(t.pickupStopsJson);
        const sc = STATUS_COLOR[t.status] || '#939598';
        const color = DEBRIS_TYPE_COLORS[t.debrisType] || '#7B7D7F';
        const isDraft = t.status === 'draft';
        const isInTransit = t.status === 'in_transit';

        return (
          <div key={t.id} style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 4,
            marginBottom: 16, overflow: 'hidden', borderLeft: `4px solid ${color}`,
          }}>
            {/* Header — tappable to open detail */}
            <div onClick={() => setDetailTicket(t)} style={{ padding: '16px 14px 8px', display: 'flex', justifyContent: 'space-between', gap: 8, cursor: 'pointer' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C' }}>
                  {t.ticketNumber || `Ticket #${t.id}`}
                </div>
                <div style={{ fontSize: 12, color: '#7B7D7F', marginTop: 2 }}>
                  {t.truckNumber ? `Truck ${t.truckNumber}` : 'No truck'}
                  {t.debrisType ? ` · ${DEBRIS_TYPE_LABELS[t.debrisType] || t.debrisType}` : ''}
                  {' · '}{stops.length} stop{stops.length !== 1 ? 's' : ''}
                  {t.calculatedCubicYards && parseFloat(t.calculatedCubicYards) > 0
                    ? ` · ${parseFloat(t.calculatedCubicYards).toFixed(1)} CY`
                    : ''}
                  {t.unitCount ? ` · ${t.unitCount} units` : ''}
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: sc, background: `${sc}15`, padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>
                {t.status?.replace(/_/g, ' ')}
              </span>
            </div>

            {/* Stop list with navigation links */}
            {stops.length > 0 && (
              <div onClick={() => setDetailTicket(t)} style={{ padding: '0 14px 8px', cursor: 'pointer' }}>
                {stops.map((s: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
                    <span style={{ color: '#939598', minWidth: 14, fontWeight: 700 }}>{i + 1}</span>
                    <span style={{ flex: 1, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.address || 'No address'}
                    </span>
                    {s.estimatedLoadPercent && <span style={{ color: '#d97706', fontWeight: 600, fontSize: 12 }}>{s.estimatedLoadPercent}%</span>}
                    {s.address && (
                      <a
                        href={mapsLink(s.address, s.lat, s.lng)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ color: '#4B3FFF', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                        title="Navigate"
                      >
                        <IconNavigation size={13} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Signing status */}
            {(t.loadingMonitorSignature || t.driverPickupSignature) && (
              <div style={{ padding: '4px 14px 8px', display: 'flex', gap: 8 }}>
                {t.loadingMonitorSignature && (
                  <span style={{ fontSize: 12, color: '#059669', fontWeight: 700 }}>✓ Monitor signed</span>
                )}
                {t.driverPickupSignature && (
                  <span style={{ fontSize: 12, color: '#059669', fontWeight: 700 }}>✓ Driver signed</span>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div onClick={e => e.stopPropagation()} style={{ padding: '8px 14px 16px', borderTop: '1px solid #f1f5f9' }}>
              {isDraft && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setQrTicket(t)} style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '8px 8px',
                    background: '#1e293b', color: '#fff', border: 'none', borderRadius: 4,
                    fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                  }}>
                    <IconQrcode size={13} /> QR
                  </button>
                  <button onClick={() => setAddStopTicket(t)} style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '8px 8px',
                    background: '#f1f5f9', color: '#475569', border: '1.5px solid #e2e8f0',
                    borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                    <IconPlus size={13} /> Add Stop
                  </button>
                  <button onClick={() => setCloseOutTicket(t)} style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '8px 8px',
                    background: '#d97706', color: '#fff', border: 'none',
                    borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                    <IconTruck size={13} /> Close Out
                  </button>
                </div>
              )}
              {isInTransit && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setQrTicket(t)} style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '8px 8px',
                    background: '#1e293b', color: '#fff', border: 'none', borderRadius: 4,
                    fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                  }}>
                    <IconQrcode size={13} /> QR
                  </button>
                  <button onClick={() => setQrTicket(t)} style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '8px 8px',
                    background: '#059669', color: '#fff', border: 'none',
                    borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                    <IconQrcode size={13} /> Show QR to Monitor
                  </button>
                </div>
              )}
              {!isDraft && !isInTransit && (
                <button onClick={() => setQrTicket(t)} style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 8px',
                  background: '#1e293b', color: '#fff', border: 'none', borderRadius: 4,
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  <IconQrcode size={13} /> QR
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Completed tickets */}
      {completedTickets.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={() => setShowCompleted(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none',
              fontSize: 14, fontWeight: 700, color: '#7B7D7F', cursor: 'pointer', padding: '8px 0',
            }}>
            {showCompleted ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            Completed ({completedTickets.length})
          </button>
          {showCompleted && completedTickets.map(t => {
            const sc = STATUS_COLOR[t.status] || '#939598';
            return (
              <div key={t.id} onClick={() => setDetailTicket(t)} style={{
                background: '#F8F8F8', border: '1px solid #e2e8f0', borderRadius: 4,
                padding: '8px 16px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer',
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#475569' }}>{t.ticketNumber || `#${t.id}`}</div>
                  <div style={{ fontSize: 12, color: '#939598' }}>
                    {t.truckNumber ? `Truck ${t.truckNumber}` : ''}
                    {t.calculatedCubicYards && parseFloat(t.calculatedCubicYards) > 0
                      ? ` · ${parseFloat(t.calculatedCubicYards).toFixed(1)} CY` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => setQrTicket(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#939598', display: 'flex' }}>
                    <IconQrcode size={16} />
                  </button>
                  <span style={{ fontSize: 12, fontWeight: 700, color: sc, background: `${sc}15`, padding: '2px 7px', borderRadius: 4 }}>
                    {t.status?.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Drawers */}
      {showNewTicket  && <NewTicketDrawer  zone={zone} onClose={() => setShowNewTicket(false)} />}
      {qrTicket       && <QRDrawer         ticket={qrTicket} onClose={() => setQrTicket(null)} />}
      {addStopTicket  && <AddStopDrawer    ticket={addStopTicket} onClose={() => setAddStopTicket(null)} />}
      {closeOutTicket && <CloseOutDrawer   ticket={closeOutTicket} onClose={() => setCloseOutTicket(null)} />}

      {/* Ticket detail full-screen */}
      {detailTicket && (
        <TicketDetail
          ticket={detailTicket}
          zone={zone}
          role={role}
          onClose={() => setDetailTicket(null)}
          onAddStop={() => { setAddStopTicket(detailTicket); setDetailTicket(null); }}
          onCloseOut={() => { setCloseOutTicket(detailTicket); setDetailTicket(null); }}
          onShowQR={() => { setQrTicket(detailTicket); setDetailTicket(null); }}
        />
      )}
    </div>
  );
}
